import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import { useOffline } from '../contexts/OfflineContext';
import api from '../services/api';
import { offlineDB } from '../services/offlineDB';
import ShareModal from '../components/ShareModal';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import Logo from '../components/Logo';
import BurgerMenu from '../components/BurgerMenu';
import OfflineIndicator from '../components/OfflineIndicator';
import { WebSocketStatus } from '../components/WebSocketStatus';
import LinkPreview from '../components/LinkPreview';
import { CategoryPicker } from '../components/CategoryPicker';
import { ItemEditModal } from '../components/ItemEditModal';
import { SupermarketView } from '../components/SupermarketView';
import { renderTextWithLinks } from '../utils/linkParser';
import { ShoppingList, ListItem } from '../types';
import { getCategory, getAllCategories, type Category } from '../utils/categories';
import { getCategoryEmoji } from '../utils/categoryIcons';
import { extractAndSaveCustomCategories } from '../utils/supermarketCategories';
import { supermarketService } from '../services/supermarketService';
import { Tag, ShoppingCart, CheckSquare, Store } from 'lucide-react';
import ChatDrawer from '../components/ChatDrawer';

export default function ListDetail() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { isOnline } = useOffline();
  const [list, setList] = useState<ShoppingList | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('');
  const [newItemDueDate, setNewItemDueDate] = useState('');
  const [newItemReminderMinutes, setNewItemReminderMinutes] = useState(0);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const [linkPreview, setLinkPreview] = useState<string | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<number | null>(null);
  const [categoryPickerItemId, setCategoryPickerItemId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'category' | 'supermarket'>('list');
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [activeUsers, setActiveUsers] = useState<{ userId: string; userName: string }[]>([]);
  const [availableSupermarkets, setAvailableSupermarkets] = useState<any[]>([]);
  const [showSupermarketPicker, setShowSupermarketPicker] = useState(false);
  
  // Double-Tap Detection für "Ausverkauft"
  const [lastTapTime, setLastTapTime] = useState<number>(0);
  const [lastTapItemId, setLastTapItemId] = useState<string | null>(null);
  const tapTimerRef = useRef<number | null>(null);
  
  // Backward compatibility: groupByCategory
  const groupByCategory = viewMode === 'category';

  useEffect(() => {
    fetchList();
  }, [listId]);

  useEffect(() => {
    if (!listId) return;
    api.post(`/lists/${listId}/items/read`).catch(() => {});
  }, [listId]);

  // Reload bei App-Resume (für Android)
  useEffect(() => {
    const handleAppResume = () => {
      console.log('📱 App resumed, reloading list data...');
      fetchList();
    };

    window.addEventListener('app-resumed', handleAppResume);
    return () => {
      window.removeEventListener('app-resumed', handleAppResume);
    };
  }, [listId]);

  // Handle offline item sync (replace temp items with real items)
  useEffect(() => {
    const handleOfflineItemSynced = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { tempId, realItem } = customEvent.detail;
      
      console.log('🔄 [ListDetail] Replacing temp item with synced item:', tempId, '→', realItem.id);
      
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        
        // Check if temp item still exists
        const tempExists = prevList.items.some(item => item.id === tempId);
        if (!tempExists) {
          console.log('ℹ️ [ListDetail] Temp item already replaced or removed');
          return prevList;
        }
        
        return {
          ...prevList,
          items: prevList.items.map(item => 
            item.id === tempId ? realItem : item
          ),
        };
      });
    };

    window.addEventListener('offline-item-synced', handleOfflineItemSynced);
    return () => {
      window.removeEventListener('offline-item-synced', handleOfflineItemSynced);
    };
  }, []);

  useEffect(() => {
    if (!socket || !listId) return;

    console.log('🔌 Joining list room:', listId);
    
    // WICHTIG: Alte Listener entfernen BEVOR wir neue registrieren
    socket.off('item_added');
    socket.off('item_updated');
    socket.off('item_deleted');
    socket.off('member_removed');
    socket.off('active_users_updated');
    
    // Join list room
    socket.emit('join_list', listId);

    // Listen for real-time updates
    socket.on('item_added', (data) => {
      console.log('📥 item_added event:', data);
      
      // Get current user ID
      const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null;
      
      // Ignore own events - we already handled them via HTTP response
      if (data._sourceUserId === currentUserId) {
        console.log('ℹ️ Ignoring own item_added event (already in UI from HTTP response)');
        return;
      }
      
      // Item from another user - add it
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        
        // Safety check: prevent duplicates
        const existingItem = prevList.items.find(item => item.id === data.id);
        if (existingItem) {
          console.log('ℹ️ Item already exists, skipping duplicate');
          return prevList;
        }
        
        console.log('✅ Adding item from another user:', data.name);
        return {
          ...prevList,
          items: [...prevList.items, data],
        };
      });
    });

    socket.on('item_updated', (data) => {
      console.log('📥 item_updated event:', data);
      
      // Get current user ID
      const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null;
      
      // Ignore own events - we already handled them via HTTP response
      if (data._sourceUserId === currentUserId) {
        console.log('ℹ️ Ignoring own item_updated event (already in UI from HTTP response)');
        return;
      }
      
      console.log('✅ Updating item from another user:', data.name);
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.map(item => 
            item.id === data.id ? { ...item, ...data } : item
          ),
        };
      });
    });

    socket.on('item_deleted', (data) => {
      console.log('📥 item_deleted event:', data);
      
      // Get current user ID
      const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null;
      
      // Ignore own events - we already handled them via HTTP response
      if (data._sourceUserId === currentUserId) {
        console.log('ℹ️ Ignoring own item_deleted event (already removed from UI)');
        return;
      }
      
      console.log('✅ Deleting item from another user');
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.filter(item => item.id !== data.id),
        };
      });
    });

    socket.on('member_removed', (data) => {
      console.log('📥 member_removed event:', data);
      // If current user was removed, redirect to home
      const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null;
      if (data.userId === currentUserId) {
        setToast({ message: 'Du wurdest aus der Liste entfernt', type: 'warning' });
        setTimeout(() => navigate('/'), 2000);
      } else {
        // Update member list
        fetchList();
      }
    });

    socket.on('active_users_updated', (data) => {
      console.log('📥 active_users_updated event:', data);
      const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null;
      // Filter out current user from active users list
      const otherUsers = data.users.filter((u: { userId: string; userName: string }) => u.userId !== currentUserId);
      setActiveUsers(otherUsers);
    });

    return () => {
      socket.emit('leave_list', listId);
      socket.off('item_added');
      socket.off('item_updated');
      socket.off('item_deleted');
      socket.off('member_removed');
    };
  }, [socket, listId]);

  const fetchList = async () => {
    try {
      const response = await api.get(`/lists/${listId}`);
      setList(response.data.data);
      
      // Extract and save custom categories from supermarket if present
      if (response.data.data?.supermarket) {
        extractAndSaveCustomCategories(response.data.data.supermarket);
      }
    } catch (error) {
      console.error('Error fetching list:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const addItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    const itemName = newItemName;
    const itemQuantity = newItemQuantity;
    const itemDueDate = newItemDueDate;
    const itemReminderMinutes = newItemReminderMinutes;

    // Generate unique temp ID (collision-safe)
    const tempId = `temp-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;

    // Convert datetime-local to ISO string
    let dueDateISO: string | undefined = undefined;
    if (itemDueDate) {
      const [datePart, timePart] = itemDueDate.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      dueDateISO = new Date(year, month - 1, day, hour, minute).toISOString();
    }

    try {
      // Optimistic UI Update - funktioniert online UND offline
      const tempItem: any = {
        id: tempId,
        name: itemName,
        quantity: itemQuantity.trim() || null,
        notes: null,
        category: null,
        isChecked: false,
        checkedAt: null,
        checkedById: null,
        order: list?.items?.length || 0,
        dueDate: dueDateISO || null,
        reminderMinutes: itemReminderMinutes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Sofort in UI anzeigen
      setList(prevList => {
        if (!prevList) return prevList;
        return {
          ...prevList,
          items: [...(prevList.items || []), tempItem],
        };
      });

      // Input clearen - SOFORT, nicht erst nach Backend-Response
      setNewItemName('');
      setNewItemQuantity('');
      setNewItemDueDate('');
      setNewItemReminderMinutes(0);
      setShowDueDatePicker(false);

      // Backend-Call
      const response = await api.post('/items', {
        listId,
        name: itemName,
        quantity: itemQuantity.trim() || undefined,
        dueDate: dueDateISO,
        reminderMinutes: itemReminderMinutes || undefined,
      });

      // Echte ID vom Backend ersetzen
      const realItem = response.data.data;
      
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        // Replace temp item with real item from backend
        return {
          ...prevList,
          items: prevList.items.map(item => 
            item.id === tempId ? realItem : item
          ),
        };
      });

    } catch (error: any) {
      console.log('❌ Error adding item (likely offline):', error?.message);
      // Bei Offline-Fehler: Item in Offline-Queue speichern
      if (!navigator.onLine || error?.message?.includes('Network Error')) {
        await offlineDB.addToQueue({
          type: 'CREATE',
          entity: 'item',
          tempItemId: tempId,  // Track which UI item this is
          data: {
            listId,
            name: itemName,
            quantity: itemQuantity.trim() || undefined,
            dueDate: dueDateISO,
            reminderMinutes: itemReminderMinutes || undefined,
          },
        });
        console.log('💾 Item saved to offline queue with tempId:', tempId);
      } else {
        // Bei echtem Fehler: temp-Item wieder entfernen
        setList(prevList => {
          if (!prevList || !prevList.items) return prevList;
          return {
            ...prevList,
            items: prevList.items.filter(item => item.id !== tempId),
          };
        });
      }
      // Item bleibt in UI (wird bei Reconnect synchronisiert)
    }
  };
  // Group items by category
  const getGroupedItems = (items: ListItem[], checked: boolean) => {
    const filteredItems = items.filter(item => item.isChecked === checked && !item.isUnavailable);
    
    if (!groupByCategory || list?.listType !== 'SHOPPING') {
      return { 'Alle': filteredItems };
    }

    const grouped: Record<string, ListItem[]> = {};
    const categories = getAllCategories();
    
    // Initialize all category groups
    categories.forEach(([categoryName]) => {
      grouped[categoryName] = [];
    });
    grouped['Sonstiges'] = [];

    // Group items
    filteredItems.forEach(item => {
      const categoryName = item.category || 'Sonstiges';
      if (grouped[categoryName]) {
        grouped[categoryName].push(item);
      } else {
        grouped['Sonstiges'].push(item);
      }
    });

    // Remove empty groups
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0) {
        delete grouped[key];
      }
    });

    return grouped;
  };
  const toggleItem = async (itemId: string, isChecked: boolean) => {
    const newChecked = !isChecked;

    // Optimistic UI Update
    setList(prevList => {
      if (!prevList || !prevList.items) return prevList;
      return {
        ...prevList,
        items: prevList.items.map(item =>
          item.id === itemId ? { ...item, isChecked: newChecked } : item
        ),
      };
    });

    try {
      await api.patch(`/items/${itemId}`, { isChecked: newChecked });
    } catch (error: any) {
      if (!navigator.onLine || error?.message?.includes('Network Error')) {
        // Offline: in Queue speichern, UI-State bleibt
        await offlineDB.addToQueue({
          type: 'UPDATE',
          entity: 'item',
          data: { id: itemId, isChecked: newChecked },
        });
        console.log('💾 toggleItem saved to offline queue:', itemId);
      } else {
        // Echter Fehler: Rollback
        console.error('Error toggling item:', error);
        setList(prevList => {
          if (!prevList || !prevList.items) return prevList;
          return {
            ...prevList,
            items: prevList.items.map(item =>
              item.id === itemId ? { ...item, isChecked } : item
            ),
          };
        });
      }
    }
  };

  const deleteItem = async (itemId: string) => {
    // Optimistic UI Update
    setList(prevList => {
      if (!prevList || !prevList.items) return prevList;
      return {
        ...prevList,
        items: prevList.items.filter(item => item.id !== itemId),
      };
    });

    try {
      await api.delete(`/items/${itemId}`);
    } catch (error: any) {
      if (!navigator.onLine || error?.message?.includes('Network Error')) {
        // Offline: in Queue speichern, UI-State bleibt (Item bleibt weg)
        await offlineDB.addToQueue({
          type: 'DELETE',
          entity: 'item',
          data: { id: itemId },
        });
        console.log('💾 deleteItem saved to offline queue:', itemId);
      } else {
        // Echter Fehler: Rollback
        console.error('Error deleting item:', error);
        fetchList();
      }
    }
  };

  const deleteCheckedItems = async () => {
    if (!list) return;
    
    const itemsToDelete = list.items?.filter(item => item.isChecked) || [];
    
    if (itemsToDelete.length === 0) {
      setToast({ message: 'Keine erledigten Artikel vorhanden', type: 'info' });
      return;
    }

    const confirmMessage = list.listType === 'TODO' 
      ? `${itemsToDelete.length} erledigte Aufgabe${itemsToDelete.length > 1 ? 'n' : ''} löschen?`
      : `${itemsToDelete.length} erledigte${itemsToDelete.length > 1 ? 'n' : ''} Artikel löschen?`;

    setConfirmDialog({
      title: 'Erledigt löschen',
      message: confirmMessage,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          // Optimistic UI Update
          setList(prevList => {
            if (!prevList || !prevList.items) return prevList;
            return {
              ...prevList,
              items: prevList.items.filter(item => !item.isChecked),
            };
          });

          // Delete all checked items
          await Promise.all(itemsToDelete.map(item => api.delete(`/items/${item.id}`)));
          
          setToast({ 
            message: list.listType === 'TODO' 
              ? `${itemsToDelete.length} Aufgabe${itemsToDelete.length > 1 ? 'n' : ''} gelöscht` 
              : `${itemsToDelete.length} Artikel gelöscht`, 
            type: 'success' 
          });
        } catch (error) {
          console.error('Error deleting checked items:', error);
          setToast({ message: 'Fehler beim Löschen', type: 'error' });
          // Bei Fehler: Liste neu laden
          fetchList();
        }
      }
    });
  };

  const changeItemCategory = async (itemId: string, itemName: string, newCategoryName: string) => {
    try {
      // Optimistic UI Update
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.map(item => 
            item.id === itemId ? { ...item, category: newCategoryName } : item
          ),
        };
      });

      // Update item category
      await api.patch(`/items/${itemId}`, { category: newCategoryName });

      // Save user mapping for future auto-detection
      await api.post('/items/category-mapping', {
        itemName,
        category: newCategoryName
      });

      setToast({ message: 'Kategorie aktualisiert', type: 'success' });
    } catch (error) {
      console.error('Error updating category:', error);
      setToast({ message: 'Fehler beim Aktualisieren', type: 'error' });
      fetchList();
    }
  };

  const toggleUnavailable = async (itemId: string, currentStatus: boolean) => {
    try {
      // Optimistic UI Update
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.map(item =>
            item.id === itemId ? { ...item, isUnavailable: !currentStatus } : item
          ),
        };
      });

      await api.patch(`/items/${itemId}/unavailable`, {
        isUnavailable: !currentStatus,
      });

      setToast({ 
        message: !currentStatus ? 'Artikel als ausverkauft markiert' : 'Artikel wieder verfügbar', 
        type: 'info' 
      });
    } catch (error) {
      console.error('Error toggling unavailable:', error);
      // Bei Fehler: Rollback
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.map(item =>
            item.id === itemId ? { ...item, isUnavailable: currentStatus } : item
          ),
        };
      });
      setToast({ message: 'Fehler beim Aktualisieren', type: 'error' });
    }
  };

  const handleItemClick = (itemId: string, item: ListItem) => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;
    
    // Double-Tap Detection (innerhalb 300ms)
    if (timeDiff < 300 && lastTapItemId === itemId) {
      // Double-Tap erkannt - Abbrechen des pending single-tap
      if (tapTimerRef.current) {
        clearTimeout(tapTimerRef.current);
        tapTimerRef.current = null;
      }
      
      // Toggle Unavailable
      toggleUnavailable(itemId, item.isUnavailable || false);
      setLastTapTime(0);
      setLastTapItemId(null);
    } else {
      // Single-Tap - Verzögert ausführen (auf Double-Tap warten)
      setLastTapTime(now);
      setLastTapItemId(itemId);
      
      // Bestehenden Timer abbrechen
      if (tapTimerRef.current) {
        clearTimeout(tapTimerRef.current);
      }
      
      // Neuen Timer für Single-Tap setzen
      tapTimerRef.current = setTimeout(() => {
        toggleItem(itemId, item.isChecked);
        tapTimerRef.current = null;
      }, 300);
    }
  };

  const loadSupermarkets = async () => {
    try {
      const data = await supermarketService.getAll();
      setAvailableSupermarkets(data);
      
      // Extract and save custom categories from all available supermarkets
      data.forEach(supermarket => {
        extractAndSaveCustomCategories(supermarket);
      });
    } catch (error) {
      console.error('Error loading supermarkets:', error);
    }
  };

  const assignSupermarket = async (supermarketId: string | null) => {
    try {
      if (supermarketId) {
        await api.post(`/lists/${listId}/supermarket`, { supermarketId });
        setToast({ message: 'Supermarkt zugeordnet', type: 'success' });
        
        // Extract and save custom categories from the assigned supermarket
        const supermarket = availableSupermarkets.find(sm => sm.id === supermarketId);
        if (supermarket) {
          extractAndSaveCustomCategories(supermarket);
        }
      } else {
        await api.delete(`/lists/${listId}/supermarket`);
        setToast({ message: 'Supermarkt entfernt', type: 'success' });
      }
      await fetchList();
      setShowSupermarketPicker(false);
    } catch (error: any) {
      console.error('Error assigning supermarket:', error);
      setToast({ 
        message: error.response?.data?.error || 'Fehler beim Zuordnen', 
        type: 'error' 
      });
    }
  };

  const updateItem = async (itemId: string, name: string, quantity: string, dueDate?: string, reminderMinutes?: number) => {
    let dueDateISO: string | undefined = undefined;
    try {
      // Convert datetime-local to ISO string with timezone offset
      if (dueDate) {
        // datetime-local format: "2026-02-09T15:00"
        // Parse as local time explicitly to avoid timezone confusion
        const [datePart, timePart] = dueDate.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        const date = new Date(year, month - 1, day, hour, minute);
        dueDateISO = date.toISOString();
      }
      
      // Optimistic UI Update
      const oldItem = list?.items?.find(item => item.id === itemId);
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.map(item =>
            item.id === itemId ? { ...item, name, quantity: quantity || null, dueDate: dueDate || null, reminderMinutes: reminderMinutes ?? item.reminderMinutes } as ListItem : item
          ),
        };
      });

      // Close modal immediately
      setEditItemId(null);

      // Update item on backend (category will be auto-detected if name changed)
      const response = await api.patch(`/items/${itemId}`, {
        name,
        quantity: quantity || undefined,
        dueDate: dueDateISO,
        reminderMinutes: reminderMinutes,
      });

      // Update with backend response (includes new category if name changed)
      const updatedItem = response.data.data;
      setList(prevList => {
        if (!prevList || !prevList.items) return prevList;
        return {
          ...prevList,
          items: prevList.items.map(item =>
            item.id === itemId
              ? {
                  ...item,
                  name: updatedItem.name || name,
                  quantity: updatedItem.quantity || (quantity || null),
                  category: updatedItem.category || item.category,
                  dueDate: updatedItem.dueDate || (dueDate || null)
                } as ListItem
              : item
          ),
        };
      });

      setToast({ message: 'Gespeichert', type: 'success' });
    } catch (error: any) {
      if (!navigator.onLine || error?.message?.includes('Network Error')) {
        // Offline: in Queue speichern, optimistischer UI-State bleibt
        await offlineDB.addToQueue({
          type: 'UPDATE',
          entity: 'item',
          data: {
            id: itemId,
            name,
            quantity: quantity || undefined,
            dueDate: dueDateISO,
            reminderMinutes,
          },
        });
        console.log('💾 updateItem saved to offline queue:', itemId);
        setToast({ message: 'Offline gespeichert – wird synchronisiert', type: 'info' });
      } else {
        console.error('Error updating item:', error);
        setToast({ message: 'Fehler beim Speichern', type: 'error' });
        // Rollback
        fetchList();
      }
    }
  };

  const vibrate = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const handleItemDragStart = (itemId: string, e: React.DragEvent) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (targetItemId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItemId && draggedItemId !== targetItemId) {
      setDropTargetId(targetItemId);
    }
  };

  const handleItemDrop = async (targetItemId: string, e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedItemId || draggedItemId === targetItemId || !list || !list.items) {
      setDraggedItemId(null);
      return;
    }

    const allItems = [...list.items];
    const draggedIndex = allItems.findIndex(i => i.id === draggedItemId);
    const targetIndex = allItems.findIndex(i => i.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItemId(null);
      return;
    }

    // Optimistic UI update
    const [draggedItem] = allItems.splice(draggedIndex, 1);
    allItems.splice(targetIndex, 0, draggedItem);
    
    setList({
      ...list,
      items: allItems.map((item, index) => ({ ...item, order: index }))
    });
    setDraggedItemId(null);
    setDropTargetId(null);

    // Send new order to backend
    try {
      await api.patch(`/items/reorder`, {
        listId,
        itemOrder: allItems.map((item, index) => ({
          itemId: item.id,
          order: index
        }))
      });
    } catch (error) {
      console.error('Error reordering items:', error);
      setToast({ message: 'Fehler beim Sortieren', type: 'error' });
      fetchList();
    }
  };

  const handleItemDragEnd = () => {
    setDraggedItemId(null);
    setDropTargetId(null);
  };

  const handleItemTouchStart = (itemId: string, e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    
    const timer = window.setTimeout(() => {
      setDraggedItemId(itemId);
      vibrate();
    }, 500);
    
    setLongPressTimer(timer);
  };

  const handleItemTouchMove = (e: React.TouchEvent) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    if (draggedItemId) {
      e.preventDefault();
    }
  };

  const handleItemTouchEnd = async (targetItemId: string) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    if (draggedItemId && draggedItemId !== targetItemId && list && list.items) {
      const allItems = [...list.items];
      const draggedIndex = allItems.findIndex(i => i.id === draggedItemId);
      const targetIndex = allItems.findIndex(i => i.id === targetItemId);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const [draggedItem] = allItems.splice(draggedIndex, 1);
        allItems.splice(targetIndex, 0, draggedItem);
        
        setList({
          ...list,
          items: allItems.map((item, index) => ({ ...item, order: index }))
        });
        
        vibrate();

        try {
          await api.patch(`/items/reorder`, {
            listId,
            itemOrder: allItems.map((item, index) => ({
              itemId: item.id,
              order: index
            }))
          });
        } catch (error) {
          console.error('Error reordering items:', error);
          setToast({ message: 'Fehler beim Sortieren', type: 'error' });
          fetchList();
        }
      }
    }
    
    setDraggedItemId(null);
    setDropTargetId(null);
    setTouchStartY(null);
  };

  const removeMember = async (memberId: string) => {
    setConfirmDialog({
      title: 'Mitglied entfernen',
      message: 'Möchtest du dieses Mitglied wirklich aus der Liste entfernen?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete(`/lists/${listId}/members/${memberId}`);
          setToast({ message: 'Mitglied wurde entfernt', type: 'success' });
          fetchList();
        } catch (error) {
          console.error('Error removing member:', error);
          setToast({ message: 'Fehler beim Entfernen des Mitglieds', type: 'error' });
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!list) return null;

  const uncheckedItems = list.items?.filter(item => !item.isChecked) || [];
  const checkedItems = list.items?.filter(item => item.isChecked) || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)', paddingBottom: '5rem' }}>
      <header style={{ 
        backgroundColor: 'var(--bg-primary)',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <Logo variant="dark" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <ChatDrawer listId={listId!} socket={socket} />
              <BurgerMenu 
                currentPage="listDetail" 
                onShowMembers={() => setShowMembers(true)}
                onShare={() => setShowShareModal(true)}
                membersCount={list.members?.length}
              />
            </div>
          </div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{list.listType === 'TODO' ? '✓' : '🛒'}</span>
            {list.name}
          </h1>
          
          {activeUsers.length > 0 && (
            <div style={{
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
            }}>
              <span style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#10b981',
                display: 'inline-block',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }} />
              <span>
                {activeUsers.map(u => u.userName).join(', ')} 
                {activeUsers.length === 1 ? ' ist auch hier' : ' sind auch hier'}
              </span>
            </div>
          )}
          
          {list.description && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {list.description}
            </p>
          )}
        </div>
      </header>

      <main className="container" style={{ paddingTop: '1rem' }}>
        <form onSubmit={addItem} style={{ 
          marginBottom: '1.5rem', 
          position: 'sticky', 
          top: '7.5rem', 
          backgroundColor: 'var(--bg-primary)', 
          padding: '1rem', 
          borderRadius: '1rem', 
          boxShadow: 'var(--shadow-md)', 
          zIndex: 50,
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              type="text"
              className="input"
              placeholder={list.listType === 'TODO' ? 'Aufgabe hinzufügen...' : 'Artikel hinzufügen...'}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              style={{ flex: 1, margin: 0 }}
            />
            <button type="submit" className="btn btn-primary" style={{ minWidth: '3rem' }}>
              +
            </button>
          </div>
          <input
            type="text"
            className="input"
            placeholder={list.listType === 'TODO' ? '📝 Notiz (optional)' : '📦 Menge/Info (optional)'}
            value={newItemQuantity}
            onChange={(e) => setNewItemQuantity(e.target.value)}
            style={{ margin: 0, fontSize: '0.875rem' }}
          />
          {list.listType === 'TODO' && (
            <div style={{ marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setShowDueDatePicker(v => !v)}
                style={{
                  fontSize: '0.8rem',
                  color: newItemDueDate ? 'var(--primary)' : 'var(--text-tertiary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  fontWeight: newItemDueDate ? '600' : 'normal',
                }}
              >
                {newItemDueDate
                  ? `⏰ ${new Date(newItemDueDate).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}`
                  : '⏰ Fälligkeitsdatum hinzufügen'}
              </button>
              {showDueDatePicker && (
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <input
                    type="datetime-local"
                    className="input"
                    value={newItemDueDate}
                    onChange={(e) => setNewItemDueDate(e.target.value)}
                    style={{ margin: 0, fontSize: '0.875rem' }}
                  />
                  {newItemDueDate && (
                    <select
                      className="input"
                      value={newItemReminderMinutes}
                      onChange={(e) => setNewItemReminderMinutes(Number(e.target.value))}
                      style={{ margin: 0, fontSize: '0.875rem' }}
                    >
                      <option value={0}>🔔 Bei Fälligkeit</option>
                      <option value={15}>🔔 15 Minuten vorher</option>
                      <option value={30}>🔔 30 Minuten vorher</option>
                      <option value={60}>🔔 1 Stunde vorher</option>
                      <option value={120}>🔔 2 Stunden vorher</option>
                      <option value={1440}>🔔 1 Tag vorher</option>
                    </select>
                  )}
                  {newItemDueDate && (
                    <button
                      type="button"
                      onClick={() => { setNewItemDueDate(''); setNewItemReminderMinutes(0); setShowDueDatePicker(false); }}
                      style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                    >
                      Datum entfernen
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </form>

        {/* Supermarket Assignment */}
        {list.listType === 'SHOPPING' && (
          <div style={{
            marginBottom: '1.5rem',
            backgroundColor: 'var(--bg-primary)',
            padding: '1rem',
            borderRadius: '1rem',
            boxShadow: 'var(--shadow-sm)',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '0.25rem' }}>
                  SUPERMARKT-ROUTE
                </div>
                {list.supermarket ? (
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                    {list.supermarket.chain} {list.supermarket.name && `- ${list.supermarket.name}`}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
                      {list.supermarket.street}, {list.supermarket.zipCode} {list.supermarket.city}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                    Kein Supermarkt zugeordnet
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  if (availableSupermarkets.length === 0) {
                    loadSupermarkets();
                  }
                  setShowSupermarketPicker(!showSupermarketPicker);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {list.supermarket ? 'Ändern' : 'Zuordnen'}
              </button>
            </div>

            {showSupermarketPicker && (
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--border-color)',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {list.supermarket && (
                  <button
                    onClick={() => assignSupermarket(null)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginBottom: '0.5rem',
                      textAlign: 'left'
                    }}
                  >
                    ✕ Supermarkt entfernen
                  </button>
                )}
                {availableSupermarkets.map(sm => (
                  <button
                    key={sm.id}
                    onClick={() => assignSupermarket(sm.id)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: list.supermarket?.id === sm.id ? 'var(--primary-light)' : 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      border: `1px solid ${list.supermarket?.id === sm.id ? 'var(--primary)' : 'var(--border-color)'}`,
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      marginBottom: '0.5rem',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontWeight: '600' }}>
                      {sm.chain} {sm.name && `- ${sm.name}`}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                      {sm.street}, {sm.zipCode} {sm.city}
                    </div>
                  </button>
                ))}
                <button
                  onClick={() => navigate('/supermarkets/create')}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--primary)',
                    border: '2px dashed var(--primary)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  + Neuen Supermarkt anlegen
                </button>
              </div>
            )}
          </div>
        )}

        {uncheckedItems.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h2 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', margin: 0 }}>
                {list.listType === 'TODO' ? 'Offene Aufgaben' : 'Offene Artikel'} ({uncheckedItems.length})
              </h2>
              {list.listType === 'SHOPPING' && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setViewMode('list')}
                    style={{
                      background: viewMode === 'list' ? 'var(--primary)' : 'var(--bg-tertiary)',
                      color: viewMode === 'list' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    title="Listenansicht"
                  >
                    📋
                  </button>
                  <button
                    onClick={() => setViewMode('category')}
                    style={{
                      background: viewMode === 'category' ? 'var(--primary)' : 'var(--bg-tertiary)',
                      color: viewMode === 'category' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    title="Kategorieansicht"
                  >
                    📂
                  </button>
                  {list.supermarket && (
                    <button
                      onClick={() => setViewMode('supermarket')}
                      style={{
                        background: viewMode === 'supermarket' ? 'var(--primary)' : 'var(--bg-tertiary)',
                        color: viewMode === 'supermarket' ? 'white' : 'var(--text-secondary)',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                      title="Supermarkt-Route"
                    >
                      🏪
                    </button>
                  )}
                </div>
              )}
            </div>
            {viewMode === 'supermarket' && list.supermarket ? (
              <SupermarketView
                supermarket={list.supermarket}
                items={list.items || []}
                onToggleItem={toggleItem}
                onToggleUnavailable={toggleUnavailable}
                onEditItem={(itemId) => setEditItemId(itemId)}
              />
            ) : (
              <>
                {Object.entries(getGroupedItems(list.items || [], false)).map(([categoryName, items]) => (
              <div key={categoryName} style={{ marginBottom: groupByCategory ? '1.5rem' : 0 }}>
                {groupByCategory && categoryName !== 'Alle' && (
                  <h3 style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '600', 
                    color: 'var(--text-tertiary)', 
                    marginBottom: '0.5rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {(() => {
                      const category = getCategory(categoryName);
                      if (category) {
                        const IconComponent = category.icon;
                        return <IconComponent size={14} color={category.color} strokeWidth={2.5} />;
                      }
                      return getCategoryEmoji(categoryName);
                    })()}
                    {categoryName}
                  </h3>
                )}
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="card fade-in"
                      draggable
                      onDragStart={(e) => handleItemDragStart(item.id, e)}
                      onDragOver={(e) => handleItemDragOver(item.id, e)}
                      onDrop={(e) => handleItemDrop(item.id, e)}
                      onDragEnd={handleItemDragEnd}
                      onTouchStart={(e) => handleItemTouchStart(item.id, e)}
                      onTouchMove={handleItemTouchMove}
                      onTouchEnd={() => handleItemTouchEnd(item.id)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        padding: '1rem',
                        cursor: 'pointer',
                        opacity: draggedItemId === item.id ? 0.5 : item.isUnavailable ? 0.6 : 1,
                        transform: draggedItemId === item.id ? 'scale(0.98)' : 'scale(1)',
                        boxShadow: draggedItemId === item.id ? '0 4px 12px rgba(0, 0, 0, 0.2)' : undefined,
                        borderTop: dropTargetId === item.id ? '3px solid var(--primary)' : undefined,
                        backgroundColor: item.isUnavailable ? 'var(--bg-tertiary)' : undefined,
                        transition: 'opacity 0.2s, transform 0.2s, box-shadow 0.2s, border-top 0.15s, background-color 0.2s'
                      }}
                      onClick={() => handleItemClick(item.id, item)}
                    >
                      <div
                        style={{
                          cursor: 'grab',
                          fontSize: '1.25rem',
                          color: 'var(--text-tertiary)',
                          userSelect: 'none',
                          padding: '0.25rem'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.cursor = 'grabbing'}
                        onMouseUp={(e) => e.currentTarget.style.cursor = 'grab'}
                      >
                        ⋮⋮
                      </div>
                      <input
                        type="checkbox"
                        checked={item.isChecked}
                        onChange={() => toggleItem(item.id, item.isChecked)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          width: '1.5rem', 
                          height: '1.5rem', 
                          cursor: 'pointer',
                          accentColor: 'var(--primary)'
                        }}
                      />
                      {list.listType === 'SHOPPING' && (() => {
                        const category = item.category ? getCategory(item.category) : null;
                        const color = category?.color || '#9ca3af';
                        const title = category?.name || item.category || 'Kategorie hinzufügen';
                        
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategoryPickerItemId(item.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '28px',
                              minHeight: '28px',
                              opacity: item.category ? 1 : 0.4,
                              fontSize: category ? '0' : '1.125rem'
                            }}
                            title={title}
                          >
                            {category ? (
                              <>
                                {(() => {
                                  const IconComponent = category.icon;
                                  return <IconComponent size={18} color={color} strokeWidth={2.5} />;
                                })()}
                              </>
                            ) : item.category ? (
                              <span style={{ fontSize: '1.125rem' }}>{getCategoryEmoji(item.category)}</span>
                            ) : (
                              <Tag size={18} color={color} strokeWidth={2.5} />
                            )}
                          </button>
                        );
                      })()}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '1rem' }}>
                          {renderTextWithLinks({ text: item.name, onLinkClick: setLinkPreview })}
                        </div>
                        {item.quantity && (
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{item.quantity}</div>
                        )}
                        {list.listType === 'TODO' && item.dueDate && (() => {
                          const dueDate = new Date(item.dueDate);
                          const now = new Date();
                          const isOverdue = dueDate < now && !item.isChecked;
                          const formattedDate = dueDate.toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                          
                          return (
                            <div style={{ 
                              fontSize: '0.75rem', 
                              color: isOverdue ? '#ef4444' : 'var(--text-secondary)',
                              fontWeight: isOverdue ? '600' : '400',
                              marginTop: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <span>⏰</span>
                              <span>{formattedDate}</span>
                              {isOverdue && <span style={{ marginLeft: '0.25rem' }}>(Überfällig!)</span>}
                            </div>
                          );
                        })()}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditItemId(item.id);
                        }}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: 'var(--text-secondary)', 
                          cursor: 'pointer', 
                          fontSize: '1.5rem',
                          padding: '0.5rem',
                          borderRadius: '0.5rem',
                          transition: 'background-color 0.2s',
                          lineHeight: '1'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Bearbeiten"
                      >
                        ⋮
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
              </>
            )}
          </div>
        )}

        {checkedItems.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h2 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', margin: 0 }}>
                {list.listType === 'TODO' ? 'Erledigte Aufgaben' : 'Erledigt'} ({checkedItems.length})
              </h2>
              <button
                onClick={deleteCheckedItems}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
                title={list.listType === 'TODO' ? 'Alle erledigten Aufgaben löschen' : 'Alle erledigten Artikel löschen'}
              >
                🗑️ Alle löschen
              </button>
            </div>
            {Object.entries(getGroupedItems(list.items || [], true)).map(([categoryName, items]) => (
              <div key={categoryName} style={{ marginBottom: groupByCategory ? '1.5rem' : 0 }}>
                {groupByCategory && categoryName !== 'Alle' && list.listType === 'SHOPPING' && (
                  <h3 style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '600', 
                    color: 'var(--text-tertiary)', 
                    marginBottom: '0.5rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: 0.6
                  }}>
                    {(() => {
                      const category = getCategory(categoryName);
                      if (category) {
                        const IconComponent = category.icon;
                        return <IconComponent size={14} color={category.color} strokeWidth={2.5} />;
                      }
                      return getCategoryEmoji(categoryName);
                    })()}
                    {categoryName}
                  </h3>
                )}
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="card fade-in"
                      draggable
                      onDragStart={(e) => handleItemDragStart(item.id, e)}
                      onDragOver={(e) => handleItemDragOver(item.id, e)}
                      onDrop={(e) => handleItemDrop(item.id, e)}
                      onDragEnd={handleItemDragEnd}
                      onTouchStart={(e) => handleItemTouchStart(item.id, e)}
                      onTouchMove={handleItemTouchMove}
                      onTouchEnd={() => handleItemTouchEnd(item.id)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        padding: '1rem', 
                        opacity: draggedItemId === item.id ? 0.3 : item.isUnavailable ? 0.4 : 0.6,
                        cursor: 'pointer',
                        transform: draggedItemId === item.id ? 'scale(0.98)' : 'scale(1)',
                        boxShadow: draggedItemId === item.id ? '0 4px 12px rgba(0, 0, 0, 0.2)' : undefined,
                        borderTop: dropTargetId === item.id ? '3px solid var(--primary)' : undefined,
                        backgroundColor: item.isUnavailable ? 'var(--bg-tertiary)' : undefined,
                        transition: 'opacity 0.2s, transform 0.2s, box-shadow 0.2s, border-top 0.15s, background-color 0.2s'
                      }}
                      onClick={() => handleItemClick(item.id, item)}
                    >
                      <div
                        style={{
                          cursor: 'grab',
                          fontSize: '1.25rem',
                          color: 'var(--text-tertiary)',
                          userSelect: 'none',
                          padding: '0.25rem'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.cursor = 'grabbing'}
                        onMouseUp={(e) => e.currentTarget.style.cursor = 'grab'}
                      >
                        ⋮⋮
                      </div>
                      <input
                        type="checkbox"
                        checked={item.isChecked}
                        onChange={() => toggleItem(item.id, item.isChecked)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          width: '1.5rem', 
                          height: '1.5rem', 
                          cursor: 'pointer',
                          accentColor: 'var(--success)'
                        }}
                      />
                      {list.listType === 'SHOPPING' && (() => {
                        const category = item.category ? getCategory(item.category) : null;
                        const color = category?.color || '#9ca3af';
                        const title = category?.name || item.category || 'Kategorie hinzufügen';
                        
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategoryPickerItemId(item.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '28px',
                              minHeight: '28px',
                              opacity: item.category ? 0.6 : 0.3,
                              fontSize: category ? '0' : '1.125rem'
                            }}
                            title={title}
                          >
                            {category ? (
                              <>
                                {(() => {
                                  const IconComponent = category.icon;
                                  return <IconComponent size={18} color={color} strokeWidth={2.5} />;
                                })()}
                              </>
                            ) : item.category ? (
                              <span style={{ fontSize: '1.125rem' }}>{getCategoryEmoji(item.category)}</span>
                            ) : (
                              <Tag size={18} color={color} strokeWidth={2.5} />
                            )}
                          </button>
                        );
                      })()}
                      <div style={{ flex: 1, textDecoration: 'line-through' }}>
                        <div style={{ fontWeight: '500', fontSize: '1rem' }}>
                          {renderTextWithLinks({ text: item.name, onLinkClick: setLinkPreview })}
                        </div>
                        {item.quantity && (
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{item.quantity}</div>
                        )}
                        {list.listType === 'TODO' && item.dueDate && (() => {
                          const dueDate = new Date(item.dueDate);
                          const formattedDate = dueDate.toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                          
                          return (
                            <div style={{ 
                              fontSize: '0.75rem', 
                              color: 'var(--text-secondary)',
                              marginTop: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <span>⏰</span>
                              <span>{formattedDate}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditItemId(item.id);
                        }}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: 'var(--text-secondary)', 
                          cursor: 'pointer', 
                          fontSize: '1.5rem',
                          padding: '0.5rem',
                          borderRadius: '0.5rem',
                          transition: 'background-color 0.2s',
                          lineHeight: '1',
                          opacity: 0.6
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Bearbeiten"
                      >
                        ⋮
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Unavailable Items (nur außerhalb Supermarket-View) */}
        {viewMode !== 'supermarket' && list.items && list.items.filter(item => item.isUnavailable).length > 0 && (
          <div>
            <h2 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
              Nicht verfügbar ({list.items.filter(item => item.isUnavailable).length})
            </h2>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {list.items.filter(item => item.isUnavailable).map((item) => (
                <div
                  key={item.id}
                  className="card fade-in"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem', 
                    padding: '1rem',
                    cursor: 'pointer',
                    opacity: 0.7,
                    backgroundColor: 'var(--bg-tertiary)',
                    transition: 'opacity 0.2s, background-color 0.2s'
                  }}
                  onClick={() => handleItemClick(item.id, item)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', fontSize: '1rem', color: 'var(--text-primary)' }}>
                      🚫 {renderTextWithLinks({ text: item.name, onLinkClick: setLinkPreview })}
                    </div>
                    {item.quantity && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{item.quantity}</div>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditItemId(item.id);
                    }}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: 'var(--text-secondary)', 
                      cursor: 'pointer', 
                      fontSize: '1.5rem',
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      transition: 'background-color 0.2s',
                      lineHeight: '1'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Bearbeiten"
                  >
                    ⋮
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!list.items || list.items.length === 0) && (
          <div className="card" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            {list.listType === 'SHOPPING' ? (
              <ShoppingCart size={64} strokeWidth={1.5} style={{ margin: '0 auto 1rem', color: 'var(--text-tertiary)' }} />
            ) : (
              <CheckSquare size={64} strokeWidth={1.5} style={{ margin: '0 auto 1rem', color: 'var(--text-tertiary)' }} />
            )}
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Liste ist leer
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              {list.listType === 'SHOPPING' ? 'Füge deinen ersten Artikel hinzu' : 'Füge deine erste Aufgabe hinzu'}
            </p>
          </div>
        )}
      </main>

      {showShareModal && (
        <ShareModal
          listId={listId!}
          listName={list.name}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showShareModal && (
        <ShareModal
          listId={listId!}
          listName={list.name}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showMembers && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setShowMembers(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Mitglieder
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
              {list.members?.map(member => {
                const isOwner = member.userId === list.ownerId;
                const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null;
                const canRemove = list.ownerId === currentUserId && !isOwner;

                return (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '0.5rem',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500' }}>{member.user.name}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {member.user.email}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {isOwner && (
                        <span style={{ 
                          fontSize: '0.75rem', 
                          padding: '0.25rem 0.5rem', 
                          backgroundColor: 'var(--primary)', 
                          color: 'white', 
                          borderRadius: '0.25rem' 
                        }}>
                          Inhaber
                        </span>
                      )}
                      {canRemove && (
                        <button
                          onClick={() => removeMember(member.userId)}
                          className="btn btn-sm"
                          style={{ backgroundColor: 'var(--danger)', color: 'white' }}
                        >
                          Entfernen
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => setShowMembers(false)}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              Schließen
            </button>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <OfflineIndicator />
      <WebSocketStatus />

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {linkPreview && (
        <LinkPreview
          url={linkPreview}
          onClose={() => setLinkPreview(null)}
        />
      )}

      {categoryPickerItemId && (() => {
        const item = list?.items?.find(i => i.id === categoryPickerItemId);
        if (!item) return null;
        
        // Extract categories from supermarket zones
        const supermarketCategories = list?.supermarket?.zones
          .flatMap(zone => zone.categoryMappings.map(cm => cm.categoryName)) || [];
        
        return (
          <CategoryPicker
            currentCategory={item.category || undefined}
            onSelect={(category) => changeItemCategory(categoryPickerItemId, item.name, category)}
            onClose={() => setCategoryPickerItemId(null)}
            supermarketCategories={supermarketCategories}
          />
        );
      })()}

      {editItemId && (() => {
        const item = list?.items?.find(i => i.id === editItemId);
        if (!item) return null;
        
        return (
          <ItemEditModal
            item={item}
            listType={list?.listType || 'SHOPPING'}
            onSave={(name, quantity, dueDate, reminderMinutes) => updateItem(editItemId, name, quantity, dueDate, reminderMinutes)}
            onDelete={() => {
              deleteItem(editItemId);
              setEditItemId(null);
            }}
            onClose={() => setEditItemId(null)}
          />
        );
      })()}
    </div>
  );
}
